'use client';

import { useState, useEffect } from 'react';
import { BarChart2, Heart, User, Loader2, Users, Swords, Grid3X3, List, Trophy, Settings } from 'lucide-react';
import TradeCard, { TradeCardData } from '@/app/home-feed/components/TradeCard';
import SettingsPanel from '@/app/settings/SettingsPanel';
import IdeaTypeBadge from '@/components/common/IdeaTypeBadge';
import Link from 'next/link';
import { ACHIEVEMENTS } from '@/lib/gamification';
import { useLang } from '@/lib/i18n/LanguageContext';

interface ProfileTabsProps {
  userId: string;
  username: string;
  bio: string;
  // Owner-only "Podešavanja" tab (profile + settings are one page) and ?tab= deep link.
  isCurrentUser?: boolean;
  initialTab?: string | null;
  achievements?: Array<{ achievement: string; unlockedAt: string }>;
  stats: {
    totalTrades: number;
    openTrades: number;
    closedTrades?: number;
    analysisCount?: number;
    winRate: number;
    followers: number;
    following: number;
    likesReceived: number;
    totalPnlR: number;
    performanceScore?: number;
    winCount?: number;
    lossCount?: number;
    beCount?: number;
    avgRR?: number;
    consistencyScore?: number;
    accuracyScore?: number;
    battlesWon?: number;
    battlesLost?: number;
    battleWinRate?: number;
  };
}

// ── Image-first trade grid card ─────────────────────────────────────────────
function TradeGridCard({ trade }: { trade: TradeCardData }) {
  const isBuy = trade.direction === 'BUY';
  const statusColors: Record<string, { bg: string; color: string }> = {
    OPEN: { bg: 'var(--info)',    color: 'white' },
    WIN:  { bg: 'var(--bullish)', color: 'white' },
    LOSS: { bg: 'var(--bearish)', color: 'white' },
    BE:   { bg: 'var(--win)',     color: 'white' },
  };
  const sc = statusColors[trade.status] ?? statusColors.OPEN;

  // Calculate R:R
  const risk = isBuy ? trade.entryPrice - trade.stopLoss : trade.stopLoss - trade.entryPrice;
  const reward = isBuy ? trade.takeProfit1 - trade.entryPrice : trade.entryPrice - trade.takeProfit1;
  const rr = risk > 0 && reward > 0 ? (reward / risk).toFixed(1) : null;

  return (
    <Link
      href={`/ideas/${trade.id}`}
      className="group relative block rounded-xl overflow-hidden cursor-pointer transition-transform duration-200 hover:scale-[1.02]"
      style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', aspectRatio: '4/3' }}
    >
      {/* Chart image or fallback */}
      {trade.imageUrl ? (
        <img
          src={trade.imageUrl}
          alt={`${trade.ticker} ${isBuy ? 'LONG' : 'SHORT'} chart`}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        /* Synthetic chart placeholder */
        <div className="w-full h-full flex flex-col items-center justify-center gap-2 relative overflow-hidden"
          style={{ background: 'var(--bg-sunken)' }}>
          {/* Grid lines */}
          <div className="absolute inset-0 opacity-60" style={{
            backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 20px, var(--border) 20px, var(--border) 21px), repeating-linear-gradient(90deg, transparent, transparent 20px, var(--border) 20px, var(--border) 21px)'
          }} />
          {/* Fake candle bars — sparkline stroke follows direction */}
          <div className="flex items-end gap-1 h-16 px-4 w-full">
            {[0.4, 0.7, 0.5, 0.9, 0.6, 0.8, 0.45, 0.75, 0.55, 0.85, 0.65, 0.95].map((h, i) => (
              <div key={i} className="flex-1 rounded-sm" style={{
                height: `${h * 100}%`,
                background: isBuy ? 'var(--bullish)' : 'var(--bearish)',
                opacity: i % 3 === 0 ? 0.4 : 0.75,
              }} />
            ))}
          </div>
          {/* Entry/SL/TP lines */}
          <div className="absolute inset-0 flex flex-col justify-center gap-2 px-2">
            <div className="w-full h-px opacity-60" style={{ background: 'var(--bullish)', marginTop: '-8px' }} />
            <div className="w-full h-px opacity-80" style={{ background: 'var(--win)', marginTop: '4px' }} />
            <div className="w-full h-px opacity-60" style={{ background: 'var(--bearish)', marginTop: '8px' }} />
          </div>
          <div className="relative z-10 text-center">
            <p className="text-xs font-bold font-mono" style={{ color: 'var(--text-secondary)' }}>{trade.ticker}</p>
          </div>
        </div>
      )}

      {/* Always-visible idea-type marker — the grid mixes signals + analyses (esp. the Liked tab). */}
      <div className="absolute top-1.5 left-1.5">
        <IdeaTypeBadge ideaType={trade.ideaType} onImage />
      </div>

      {/* Overlay on hover */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col justify-between p-2"
        style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 60%, transparent 100%)' }}>
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold px-1.5 py-0.5 rounded" style={{ background: sc.bg, color: sc.color }}>
            {trade.status}
          </span>
          {rr && (
            <span className="text-xs font-mono font-bold px-1.5 py-0.5 rounded" style={{ background: 'rgba(0,0,0,0.7)', color: parseFloat(rr) >= 2 ? '#3ec97a' : 'var(--win-bright)' }}>
              {rr}R
            </span>
          )}
        </div>
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-sm font-bold font-mono" style={{ color: 'white' }}>{trade.ticker}</span>
            <span className="text-xs font-bold px-1.5 py-0.5 rounded" style={{
              background: isBuy ? 'rgba(14,138,77,0.85)' : 'rgba(217,56,67,0.85)',
              color: 'white',
            }}>
              {isBuy ? '▲ LONG' : '▼ SHORT'}
            </span>
          </div>
          <div className="flex items-center gap-2 text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
            <span>❤ {trade.likeCount}</span>
            <span>💬 {trade.commentCount}</span>
          </div>
        </div>
      </div>

      {/* Always-visible bottom strip */}
      <div className="absolute bottom-0 left-0 right-0 px-2 py-1.5 flex items-center justify-between"
        style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)' }}>
        <div className="flex items-center gap-1">
          <span className="text-xs font-bold font-mono" style={{ color: 'white' }}>{trade.ticker}</span>
          <span className="text-xs" style={{ color: isBuy ? '#3ec97a' : '#ff6b73' }}>
            {isBuy ? '▲' : '▼'}
          </span>
        </div>
        <span className="text-xs px-1.5 py-0.5 rounded font-bold" style={{ background: sc.bg, color: sc.color, fontSize: '10px' }}>
          {trade.status}
        </span>
      </div>
    </Link>
  );
}

export default function ProfileTabs({ userId, username, bio, stats, achievements = [], isCurrentUser = false, initialTab }: ProfileTabsProps) {
  const { t } = useLang();
  const [activeTab, setActiveTab] = useState(initialTab === 'settings' && isCurrentUser ? 'settings' : (initialTab || 'trades'));
  // ?tab=settings arriving after mount (e.g. "Edit Profile" while already on the profile)
  useEffect(() => {
    if (initialTab === 'settings' && isCurrentUser) setActiveTab('settings');
  }, [initialTab, isCurrentUser]);
  const [tradesView, setTradesView] = useState<'grid' | 'list'>('grid');

  // Trades tab state
  const [trades, setTrades] = useState<TradeCardData[]>([]);
  const [tradesLoading, setTradesLoading] = useState(false);
  const [tradesOffset, setTradesOffset] = useState(0);
  const [tradesHasMore, setTradesHasMore] = useState(false);
  const [tradesFilter, setTradesFilter] = useState('All'); // signal status bucket: All|OPEN|WIN|LOSS|BE
  const [tradeKind, setTradeKind] = useState<'SIGNAL' | 'ANALYSIS'>('SIGNAL');

  // Liked tab state
  const [likedTrades, setLikedTrades] = useState<TradeCardData[]>([]);
  const [likedLoading, setLikedLoading] = useState(false);
  const [likedOffset, setLikedOffset] = useState(0);
  const [likedHasMore, setLikedHasMore] = useState(false);

  // Followers/Following tab state
  const [followersList, setFollowersList] = useState<any[]>([]);
  const [followingList, setFollowingList] = useState<any[]>([]);
  const [socialLoading, setSocialLoading] = useState(false);
  const [socialSubTab, setSocialSubTab] = useState<'followers' | 'following'>('followers');

  // Fetch user's trades
  useEffect(() => {
    if (!userId) return;
    setTradesLoading(true);
    setTrades([]);
    setTradesOffset(0);

    const statusParam = tradeKind === 'SIGNAL' && tradesFilter !== 'All' ? `&status=${tradesFilter}` : '';
    fetch(`/api/trades?userId=${userId}&ideaType=${tradeKind}&limit=18&offset=0${statusParam}`)
      .then((r) => r.json())
      .then((data) => {
        setTrades(data.trades || []);
        setTradesHasMore(data.hasMore || false);
        setTradesOffset(18);
      })
      .catch(() => {})
      .finally(() => setTradesLoading(false));
  }, [userId, tradesFilter, tradeKind]);

  // Fetch liked trades when tab becomes active
  useEffect(() => {
    if (activeTab !== 'liked' || !username || likedTrades.length > 0) return;
    setLikedLoading(true);

    fetch(`/api/users/${encodeURIComponent(username)}/liked?limit=18&offset=0`)
      .then((r) => r.json())
      .then((data) => {
        setLikedTrades(data.trades || []);
        setLikedHasMore(data.hasMore || false);
        setLikedOffset(18);
      })
      .catch(() => {})
      .finally(() => setLikedLoading(false));
  }, [activeTab, username]);

  // Fetch followers/following when social tab is active
  useEffect(() => {
    if (activeTab !== 'social' || !username) return;
    if (followersList.length > 0 || followingList.length > 0) return;
    setSocialLoading(true);
    Promise.all([
      fetch(`/api/users/${encodeURIComponent(username)}/followers`).then((r) => r.json()).catch(() => ({ users: [] })),
      fetch(`/api/users/${encodeURIComponent(username)}/following`).then((r) => r.json()).catch(() => ({ users: [] })),
    ]).then(([followersData, followingData]) => {
      setFollowersList(followersData.users || []);
      setFollowingList(followingData.users || []);
    }).finally(() => setSocialLoading(false));
  }, [activeTab, username]);

  async function loadMoreTrades() {
    const statusParam = tradeKind === 'SIGNAL' && tradesFilter !== 'All' ? `&status=${tradesFilter}` : '';
    const res = await fetch(`/api/trades?userId=${userId}&ideaType=${tradeKind}&limit=18&offset=${tradesOffset}${statusParam}`);
    const data = await res.json();
    setTrades((prev) => [...prev, ...(data.trades || [])]);
    setTradesHasMore(data.hasMore || false);
    setTradesOffset((prev) => prev + 18);
  }

  async function loadMoreLiked() {
    const res = await fetch(
      `/api/users/${encodeURIComponent(username)}/liked?limit=18&offset=${likedOffset}`
    );
    const data = await res.json();
    setLikedTrades((prev) => [...prev, ...(data.trades || [])]);
    setLikedHasMore(data.hasMore || false);
    setLikedOffset((prev) => prev + 18);
  }

  const tabs = [
    { id: 'trades', label: t('Trades'), icon: <BarChart2 size={14} />, count: stats.totalTrades + (stats.analysisCount ?? 0) },
    { id: 'liked', label: t('Liked'), icon: <Heart size={14} />, count: stats.likesReceived },
    { id: 'achievements', label: t('Achievements'), icon: <Trophy size={14} />, count: achievements.length },
    { id: 'social', label: t('Social'), icon: <Users size={14} />, count: stats.followers + stats.following },
    { id: 'battles', label: t('Battles'), icon: <Swords size={14} />, count: (stats.battlesWon || 0) + (stats.battlesLost || 0) },
    { id: 'about', label: t('About'), icon: <User size={14} /> },
    ...(isCurrentUser ? [{ id: 'settings', label: t('Settings'), icon: <Settings size={14} /> }] : []),
  ];

  // ── Shared grid renderer ─────────────────────────────────────────────────
  function renderTradeGrid(tradeList: TradeCardData[], isLoading: boolean, hasMore: boolean, onLoadMore: () => void, emptyIcon: React.ReactNode, emptyText: string) {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin" style={{ color: 'var(--accent)' }} />
        </div>
      );
    }
    if (tradeList.length === 0) {
      return (
        <div className="py-12 text-center" style={{ color: 'var(--text-dim)' }}>
          <div className="mx-auto mb-3 opacity-40 flex justify-center">{emptyIcon}</div>
          <p className="text-sm">{emptyText}</p>
        </div>
      );
    }
    if (tradesView === 'grid') {
      return (
        <>
          <div className="grid grid-cols-3 gap-2">
            {tradeList.map((trade) => (
              <TradeGridCard key={trade.id} trade={trade} />
            ))}
          </div>
          {hasMore && (
            <button onClick={onLoadMore} className="btn-ghost w-full py-3 text-sm text-center mt-3">
              {t('Load more')}
            </button>
          )}
        </>
      );
    }
    return (
      <>
        <div className="flex flex-col gap-4">
          {tradeList.map((trade) => (
            <TradeCard key={trade.id} trade={trade} />
          ))}
        </div>
        {hasMore && (
          <button onClick={onLoadMore} className="btn-ghost w-full py-3 text-sm text-center mt-3">
            Load more
          </button>
        )}
      </>
    );
  }

  return (
    <div className="card-base overflow-hidden">
      {/* Tab bar */}
      <div className="flex items-center border-b overflow-x-auto" style={{ borderColor: 'var(--border)' }}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3.5 text-sm transition-all duration-150 whitespace-nowrap shrink-0 ${
              activeTab === tab.id ? 'feed-tab-active' : 'feed-tab-inactive'
            }`}
          >
            <span style={{ color: activeTab === tab.id ? 'var(--accent)' : 'var(--text-muted)' }}>
              {tab.icon}
            </span>
            {tab.label}
            {tab.count !== undefined && (
              <span
                className="text-xs px-1.5 py-0.5 rounded font-mono"
                style={{
                  background:
                    activeTab === tab.id
                      ? 'var(--accent-muted)'
                      : 'var(--bg-sunken)',
                  color: activeTab === tab.id ? 'var(--accent)' : 'var(--text-dim)',
                }}
              >
                {tab.count >= 1000
                  ? `${(tab.count / 1000).toFixed(1)}K`
                  : tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="p-4">
        {activeTab === 'trades' && (
          <div className="flex flex-col gap-4 animate-fade-in">
            {/* Filter + view toggle row */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex flex-col gap-2">
                {/* Idea type: Signals (settled, counted) vs Analyses (chart + thesis only) */}
                <div className="flex items-center gap-2 flex-wrap">
                  {([
                    { kind: 'SIGNAL', label: t('Signals'), count: stats.totalTrades },
                    { kind: 'ANALYSIS', label: t('Analyses'), count: stats.analysisCount ?? 0 },
                  ] as const).map(({ kind, label, count }) => {
                    const active = tradeKind === kind;
                    return (
                      <button key={kind} onClick={() => { setTradeKind(kind); setTradesFilter('All'); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-100"
                        style={{ background: active ? 'var(--accent-muted)' : 'var(--bg-sunken)', color: active ? 'var(--accent)' : 'var(--text-muted)', border: active ? '1px solid var(--accent)' : '1px solid var(--border)' }}>
                        {label}<span className="font-mono text-[11px]" style={{ color: active ? 'var(--accent)' : 'var(--text-dim)' }}>{count}</span>
                      </button>
                    );
                  })}
                </div>
                {/* Signal status buckets */}
                {tradeKind === 'SIGNAL' && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {([
                      { v: 'All', label: t('All'), count: stats.totalTrades, accent: '#0c5b41' },
                      { v: 'OPEN', label: t('Open'), count: stats.openTrades, accent: '#3e5fbf' },
                      { v: 'WIN', label: t('Won'), count: stats.winCount ?? 0, accent: '#0e8a4d' },
                      { v: 'LOSS', label: t('Lost'), count: stats.lossCount ?? 0, accent: '#d93843' },
                      { v: 'BE', label: 'BE', count: stats.beCount ?? 0, accent: '#b07c1f' },
                    ] as const).map(({ v, label, count, accent }) => {
                      const active = v === tradesFilter;
                      return (
                        <button key={v} onClick={() => setTradesFilter(v)}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-all duration-100"
                          style={{ background: active ? `${accent}1f` : 'var(--bg-sunken)', color: active ? accent : 'var(--text-muted)', border: active ? `1px solid ${accent}40` : '1px solid var(--border)' }}>
                          {label}<span className="font-mono text-[11px]" style={{ color: active ? accent : 'var(--text-dim)' }}>{count}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {/* Grid / List toggle */}
              <div className="flex items-center gap-1 p-1 rounded-lg" style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)' }}>
                <button
                  onClick={() => setTradesView('grid')}
                  className="p-1.5 rounded-md transition-all duration-100"
                  style={{ background: tradesView === 'grid' ? 'var(--accent-muted)' : 'transparent', color: tradesView === 'grid' ? 'var(--accent)' : 'var(--text-dim)' }}
                  title={t('Grid view')}
                >
                  <Grid3X3 size={14} />
                </button>
                <button
                  onClick={() => setTradesView('list')}
                  className="p-1.5 rounded-md transition-all duration-100"
                  style={{ background: tradesView === 'list' ? 'var(--accent-muted)' : 'transparent', color: tradesView === 'list' ? 'var(--accent)' : 'var(--text-dim)' }}
                  title={t('List view')}
                >
                  <List size={14} />
                </button>
              </div>
            </div>

            {renderTradeGrid(trades, tradesLoading, tradesHasMore, loadMoreTrades, <BarChart2 size={32} />, tradeKind === 'ANALYSIS' ? t('No analyses yet') : tradesFilter === 'All' ? t('No signals yet') : t('No matching signals yet'))}
          </div>
        )}

        {activeTab === 'liked' && (
          <div className="flex flex-col gap-4 animate-fade-in">
            <div className="flex items-center justify-between">
              <p className="text-xs" style={{ color: 'var(--text-dim)' }}>
                {t('Ideas liked by')} @{username}
              </p>
              <div className="flex items-center gap-1 p-1 rounded-lg" style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)' }}>
                <button
                  onClick={() => setTradesView('grid')}
                  className="p-1.5 rounded-md transition-all duration-100"
                  style={{ background: tradesView === 'grid' ? 'var(--accent-muted)' : 'transparent', color: tradesView === 'grid' ? 'var(--accent)' : 'var(--text-dim)' }}
                >
                  <Grid3X3 size={14} />
                </button>
                <button
                  onClick={() => setTradesView('list')}
                  className="p-1.5 rounded-md transition-all duration-100"
                  style={{ background: tradesView === 'list' ? 'var(--accent-muted)' : 'transparent', color: tradesView === 'list' ? 'var(--accent)' : 'var(--text-dim)' }}
                >
                  <List size={14} />
                </button>
              </div>
            </div>
            {renderTradeGrid(likedTrades, likedLoading, likedHasMore, loadMoreLiked, <Heart size={32} />, t('No liked trades yet'))}
          </div>
        )}

        {activeTab === 'social' && (
          <div className="animate-fade-in">
            {/* Sub-tabs */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setSocialSubTab('followers')}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150"
                style={{
                  background: socialSubTab === 'followers' ? 'var(--accent-muted)' : 'var(--bg-sunken)',
                  color: socialSubTab === 'followers' ? 'var(--accent)' : 'var(--text-muted)',
                  border: socialSubTab === 'followers' ? '1px solid var(--accent)' : '1px solid var(--border)',
                }}
              >
                {t('Followers')} <span className="font-mono text-xs ml-1">{stats.followers}</span>
              </button>
              <button
                onClick={() => setSocialSubTab('following')}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150"
                style={{
                  background: socialSubTab === 'following' ? 'var(--accent-muted)' : 'var(--bg-sunken)',
                  color: socialSubTab === 'following' ? 'var(--accent)' : 'var(--text-muted)',
                  border: socialSubTab === 'following' ? '1px solid var(--accent)' : '1px solid var(--border)',
                }}
              >
                {t('Following')} <span className="font-mono text-xs ml-1">{stats.following}</span>
              </button>
            </div>

            {socialLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={24} className="animate-spin" style={{ color: 'var(--accent)' }} />
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {(socialSubTab === 'followers' ? followersList : followingList).length === 0 ? (
                  <div className="py-12 text-center" style={{ color: 'var(--text-dim)' }}>
                    <Users size={32} className="mx-auto mb-3 opacity-40" />
                    <p className="text-sm">{socialSubTab === 'followers' ? t('No followers yet') : t('Not following anyone yet')}</p>
                  </div>
                ) : (
                  (socialSubTab === 'followers' ? followersList : followingList).map((u: any) => (
                    <div key={u.id} className="flex items-center justify-between p-3 rounded-xl" style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)' }}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold overflow-hidden shrink-0" style={{ background: u.image ? undefined : 'linear-gradient(135deg, var(--accent), var(--accent-hover))', color: 'white' }}>
                          {u.image ? <img src={u.image} alt={u.username} className="w-full h-full object-cover" /> : (u.username || 'T')[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{u.displayName || u.username}</p>
                          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>@{u.username}</p>
                        </div>
                      </div>
                      <a href={`/profile/${u.username}`} className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors" style={{ background: 'var(--accent-muted)', color: 'var(--accent)', border: '1px solid transparent' }}>
                        {t('View')}
                      </a>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'battles' && (
          <div className="animate-fade-in">
            {/* Battle stats overview */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[
                { label: t('Battles Won'), value: stats.battlesWon ?? 0, color: 'var(--bullish)' },
                { label: t('Battles Lost'), value: stats.battlesLost ?? 0, color: 'var(--bearish)' },
                { label: t('Battle Win Rate'), value: `${(stats.battleWinRate ?? 0).toFixed(0)}%`, color: (stats.battleWinRate ?? 0) >= 60 ? 'var(--bullish)' : (stats.battleWinRate ?? 0) >= 40 ? 'var(--win)' : 'var(--bearish)' },
              ].map((item) => (
                <div key={item.label} className="p-4 rounded-xl text-center" style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)' }}>
                  <div className="text-2xl font-bold font-mono mb-1" style={{ color: item.color }}>{item.value}</div>
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{item.label}</div>
                </div>
              ))}
            </div>

            {(stats.battlesWon || 0) + (stats.battlesLost || 0) === 0 ? (
              <div className="py-12 text-center" style={{ color: 'var(--text-dim)' }}>
                <Swords size={32} className="mx-auto mb-3 opacity-40" />
                <p className="text-sm">{t('No battles yet')}</p>
                <p className="text-xs mt-1">{t('Battles are created automatically when opposing trades are posted')}</p>
              </div>
            ) : (
              <div className="p-4 rounded-xl" style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)' }}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{t('Battle Record')}</span>
                  <span className="text-xs font-bold px-2 py-0.5 rounded" style={{
                    background: (stats.battleWinRate ?? 0) >= 60 ? 'var(--bullish-muted)' : 'var(--win-muted)',
                    color: (stats.battleWinRate ?? 0) >= 60 ? 'var(--bullish)' : 'var(--win)',
                  }}>
                    {(stats.battleWinRate ?? 0) >= 60 ? t('🏆 Battle Champion') : t('⚔ Battle Competitor')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-3 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                    <div className="h-full rounded-full" style={{
                      width: `${stats.battleWinRate ?? 0}%`,
                      background: 'linear-gradient(90deg, var(--bullish), var(--accent))',
                    }} />
                  </div>
                  <span className="text-xs font-mono font-semibold shrink-0" style={{ color: 'var(--bullish)' }}>
                    {stats.battlesWon ?? 0}W / {stats.battlesLost ?? 0}L
                  </span>
                </div>
              </div>
            )}

            <div className="mt-4 text-center">
              <Link href="/battles" className="text-xs font-medium transition-colors" style={{ color: 'var(--accent)' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--accent-hover)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--accent)')}>
                {t('View all battles →')}
              </Link>
            </div>
          </div>
        )}

        {activeTab === 'achievements' && (
          <div className="animate-fade-in">
            <div className="mb-4 flex items-center justify-between">
              <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                {achievements.length} / {ACHIEVEMENTS.length} {t('Unlocked')}
              </p>
              <div className="h-1.5 w-32 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${Math.round((achievements.length / ACHIEVEMENTS.length) * 100)}%`,
                    background: 'linear-gradient(90deg, var(--win), var(--accent))',
                  }}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {ACHIEVEMENTS.map((ach) => {
                const unlocked = achievements.find((a) => a.achievement === ach.id);
                return (
                  <div
                    key={ach.id}
                    className="p-4 rounded-xl flex flex-col items-center gap-2 text-center transition-all duration-200"
                    style={{
                      background: unlocked ? 'var(--win-muted)' : 'var(--bg-sunken)',
                      border: unlocked ? '1px solid var(--win)' : '1px solid var(--border)',
                      opacity: unlocked ? 1 : 0.5,
                    }}
                  >
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                      style={{
                        background: unlocked ? 'var(--bg-card)' : 'var(--border-subtle)',
                        filter: unlocked ? 'none' : 'grayscale(1)',
                      }}
                    >
                      {ach.icon}
                    </div>
                    <div>
                      <p className="text-xs font-bold" style={{ color: unlocked ? 'var(--win)' : 'var(--text-muted)' }}>
                        {ach.name}
                      </p>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-dim)' }}>{ach.description}</p>
                      {unlocked && (
                        <p className="text-xs mt-1 font-semibold" style={{ color: 'var(--bullish)' }}>
                          +{ach.xpReward} XP
                        </p>
                      )}
                    </div>
                    {!unlocked && (
                      <span className="text-xs px-2 py-0.5 rounded" style={{ background: 'var(--border)', color: 'var(--text-muted)' }}>
                        {t('Locked')}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'settings' && isCurrentUser && (
          <div className="animate-fade-in max-w-2xl">
            <SettingsPanel embedded />
          </div>
        )}

        {activeTab === 'about' && (
          <div className="animate-fade-in max-w-2xl">
            {/* Posted-Idea Score (legacy — based on public TradeIdea posts, not the demo account) */}
            {(stats.performanceScore ?? 0) > 0 && (
              <div className="p-5 rounded-xl mb-4" style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)' }}>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{t('Posted-Idea Score')}</h3>
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: 'var(--border-subtle)', color: 'var(--text-muted)' }}>{t('LEGACY')}</span>
                </div>
                <p className="text-xs mb-4" style={{ color: 'var(--text-dim)' }}>{t('From your public posted ideas. Your simulated account performance is shown on the Performance card.')}</p>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('Overall Score')}</span>
                  <span className="text-2xl font-bold font-mono" style={{ color: (stats.performanceScore ?? 0) >= 60 ? 'var(--bullish)' : (stats.performanceScore ?? 0) >= 40 ? 'var(--win)' : 'var(--info)' }}>
                    {(stats.performanceScore ?? 0).toFixed(0)}<span className="text-sm font-normal" style={{ color: 'var(--text-dim)' }}>/100</span>
                  </span>
                </div>
                <div className="w-full h-2.5 rounded-full overflow-hidden mb-4" style={{ background: 'var(--border)' }}>
                  <div className="h-full rounded-full" style={{ width: `${Math.min(100, stats.performanceScore ?? 0)}%`, background: 'linear-gradient(90deg, var(--accent), var(--bullish))' }} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: t('Idea Win Rate'), value: `${stats.winRate}%`, color: stats.winRate >= 60 ? 'var(--bullish)' : 'var(--win)' },
                    { label: t('Idea Avg R:R'), value: (stats.avgRR ?? 0) > 0 ? `${(stats.avgRR ?? 0).toFixed(1)}R` : '—', color: (stats.avgRR ?? 0) >= 2 ? 'var(--bullish)' : 'var(--win)' },
                    { label: t('Consistency'), value: `${(stats.consistencyScore ?? 0).toFixed(0)}%`, color: 'var(--info)' },
                    { label: t('Accuracy'), value: `${(stats.accuracyScore ?? 0).toFixed(0)}%`, color: 'var(--accent)' },
                  ].map((item) => (
                    <div key={item.label} className="p-3 rounded-xl" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                      <div className="text-xs mb-1" style={{ color: 'var(--text-dim)' }}>{item.label}</div>
                      <div className="text-lg font-bold font-mono" style={{ color: item.color }}>{item.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Posted-idea stats summary (public TradeIdea posts — separate from the demo account) */}
            <div className="p-5 rounded-xl mb-4" style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)' }}>
              <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>{t('Posted-Idea Stats')}</h3>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: t('Posted Ideas'), value: stats.totalTrades.toLocaleString() },
                  { label: t('Closed Ideas'), value: (stats.closedTrades ?? 0).toLocaleString() },
                  { label: t('Open Ideas'), value: stats.openTrades },
                  { label: t('Wins'), value: stats.winCount ?? 0 },
                  { label: t('Losses'), value: stats.lossCount ?? 0 },
                  { label: t('Break Even'), value: stats.beCount ?? 0 },
                  { label: t('Followers'), value: stats.followers >= 1000 ? `${(stats.followers / 1000).toFixed(1)}K` : stats.followers },
                  { label: t('Following'), value: stats.following },
                  { label: t('Likes Received'), value: stats.likesReceived >= 1000 ? `${(stats.likesReceived / 1000).toFixed(1)}K` : stats.likesReceived },
                ].map((item) => (
                  <div key={item.label}>
                    <div className="text-xs mb-1" style={{ color: 'var(--text-dim)' }}>{item.label}</div>
                    <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{item.value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Bio */}
            {bio && (
              <div className="p-5 rounded-xl" style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)' }}>
                <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>{t('About')}</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--text-body)' }}>{bio}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}